import { EventProperties } from '@calm/evexia-analytics';
import {
    DS_COLOR,
    DS_HEIGHT,
    DS_SHAPE_BORDER_RADIUS,
} from '@calm/evexia-design-tokens';
import { CircleConsentTypes } from '@calm/evexia-web-sdk';
import { BackButtonView } from '@rhg/components/back-button';
import { ConsentDisplay } from '@rhg/components/consent-display';
import { InvertedButton } from '@rhg/components/inverted-button';
import { FullWidthLinearProgress } from '@rhg/components/ui/progress';
import { usePostSubmitStep } from '@rhg/hooks/registration/use-next-step';
import {
    getConsentSubmission,
    getNextConsentType,
    getNextConsentTypeInFlow,
} from '@rhg/hooks/use-consent-context';
import {
    ConsentFlow,
    ConsentTypes,
} from '@rhg/hooks/use-consent-context/types';
import { useCurrentLanguage } from '@rhg/hooks/use-current-language';
import useHasAcceptedCohortTos from '@rhg/hooks/use-has-accepted-tos';
import { useMostRecentSponsorship } from '@rhg/hooks/use-most-recent-sponsorship';
import {
    // Search these to see why they are commented out
    // getConsentRegistrationStep,
    // getConsentRegistrationStepMiraculouslyFails,
    useProvidedRegistrationFlowContext,
} from '@rhg/hooks/use-registration-context';
import {
    // ConsentTypeRegistrationStepMap,
    getConsentRegistrationStepMiraculouslyWorks,
    RegistrationFlow,
    RegistrationFlowStep,
} from '@rhg/hooks/use-registration-context/types';
import { useStoredSSOTokenPartnerEligibility } from '@rhg/hooks/use-sso-flow';
import { useUpdateConsent } from '@rhg/hooks/use-update-consent';
import commonMessages from '@rhg/lang/messages/common';
import { logger } from '@rhg/libs/logger';
import { Button, Theme } from '@rhg/libs/material';
import NextImage from '@rhg/libs/next/image';
import { useHipaaSignature } from '@rhg/providers/registration/hipaa-signature';
import { routes } from '@rhg/utils/routes';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { RegistrationFlowContainer } from '../container';
import { ConsentRequiredDialog } from './consent-required-dialog';
import consentStepMessages from './messages';

const styleCancelButton = {
    color: (theme: Theme) =>
        theme.palette.mode === 'light' // This conditional is backwards because it is applied on an inverted theme
            ? DS_COLOR.DARK_PRIMARY_MAIN
            : DS_COLOR.LIGHT_PRIMARY_MAIN,
};

const styleGrow = { flexGrow: 1 };

type ConsentMessage = 'accept' | 'heading' | 'title' | 'srTitle';
const consentTypeMessages: Record<
    ConsentMessage,
    Partial<Record<CircleConsentTypes, typeof consentStepMessages.accept>> &
        Record<'default', typeof consentStepMessages.accept>
> = {
    accept: {
        gina: consentStepMessages.accept,
        parental: consentStepMessages.accept,
        default: consentStepMessages.consent,
    },
    heading: {
        gina: consentStepMessages.authorizationHeading,
        parental: consentStepMessages.parentalConsentHeading,
        default: consentStepMessages.consentHeading,
    },
    title: {
        gina: consentStepMessages.consentTitle,
        parental: consentStepMessages.parentalConsentHeading,
        default: consentStepMessages.sponsoredConsentTitle,
    },
    srTitle: {
        gina: consentStepMessages.ginaSrTitle,
        parental: consentStepMessages.parentalConsentHeading,
        default: consentStepMessages.chdppSrTitle,
    },
};

export const ParentalConsentImage = () => (
    <NextImage
        width={0}
        height={DS_HEIGHT.AVATAR_MEDIUM}
        sizes="100vw"
        style={{
            width: 'auto',
            borderRadius: `${DS_SHAPE_BORDER_RADIUS.LARGE}px`,
        }}
        data-testid="LOGO"
        alt=""
        src={'/images/age-and-gender.jpg'}
    />
);

/** Use this function to get the correct message for the consent type */
function getConsentTypeMessage(
    message: ConsentMessage,
    consentType: CircleConsentTypes
) {
    return (
        consentTypeMessages[message][consentType] ??
        consentTypeMessages[message].default
    );
}

function getCancelVariant(consentType: CircleConsentTypes) {
    if (consentType === 'gina') {
        return 'outlined';
    }

    return 'contained';
}

function Actions({
    consentType,
    showCancel,
    onCancel,
    trackProps,
}: {
    consentType: CircleConsentTypes;
    showCancel: boolean;
    onCancel: () => void;
    trackProps: EventProperties;
}) {
    const { formatMessage } = useIntl();
    const {
        changeStep,
        submitFlow,
        submitIsPending,
        setHasAcceptedConsent,
        hasAcceptedConsent,
        registrationFlow,
        isSSO,
        consentRequirements,
        accountConsents,
        isPostEligibility,
        isFetched: isRegistrationFlowFetched,
    } = useProvidedRegistrationFlowContext();
    const { getNextStep, isFetched: arePostSubmitStepPrerequisitesFetched } =
        usePostSubmitStep();
    const { isEligible: isSSOEligible, isFetched: isSSOFetched } =
        useStoredSSOTokenPartnerEligibility();
    const [hipaaSignature] = useHipaaSignature();
    const { updateConsentAsync } = useUpdateConsent();
    const { refetch: refetchMostRecentSponsorship } =
        useMostRecentSponsorship();
    const { refetch: refetchConsent } = useHasAcceptedCohortTos();

    const isFetched =
        (!isSSO || isSSOFetched) &&
        isRegistrationFlowFetched &&
        arePostSubmitStepPrerequisitesFetched;

    const onAgree = async () => {
        try {
            setHasAcceptedConsent(consentType, true);

            const consentTypes = [
                ...ConsentFlow,
                ConsentTypes.HealthcarePortability,
            ].reduce<ConsentTypes[]>((addedTypes, flowType) => {
                if (
                    // Include healthcare portability for change and loss of sponsorship flows
                    (flowType !== ConsentTypes.HealthcarePortability ||
                        [
                            RegistrationFlow.ChangeOfSponsorship,
                            RegistrationFlow.ChangeOfInviteeSponsorship,
                            RegistrationFlow.LossOfSponsorship,
                        ].includes(registrationFlow)) &&
                    // Exclude GINA from skip and loss flows
                    (flowType !== ConsentTypes.Gina ||
                        ![
                            RegistrationFlow.SkipSponsorship,
                            RegistrationFlow.LossOfSponsorship,
                        ].includes(registrationFlow))
                ) {
                    addedTypes.push(flowType);
                }
                return addedTypes;
            }, []);

            const minConsentVersions = getConsentSubmission({
                consentRequirements,
                consentTypes,
            });

            // Next consent step after this one
            const nextConsentStep = getConsentRegistrationStepMiraculouslyWorks(
                getNextConsentType({
                    consentRequirements,
                    consentAcceptance: {
                        ...hasAcceptedConsent,
                        [consentType]: true,
                    },
                    consentFlow: getNextConsentTypeInFlow(consentType),
                })
            );

            /**
             * Incredibly these produce different results See https://calmdotcom.atlassian.net/browse/EVE-8642
             * Use with the "sends the user to partner authorization"" test in chdpp.test.tsx
             */
            // console.log('check out this crazy bug', {
            //     ginaConsentType: ConsentTypes.Gina,
            //     ConsentTypeRegistrationStepMap,
            //     stepForGinaConsentTypeViaMap:
            //         ConsentTypeRegistrationStepMap[ConsentTypes.Gina],
            //     bool:
            //         ConsentTypes.Gina &&
            //         ConsentTypeRegistrationStepMap[ConsentTypes.Gina],
            //     works: getConsentRegistrationStepMiraculouslyWorks(
            //         ConsentTypes.Gina
            //     ),
            //     fails: getConsentRegistrationStepMiraculouslyFails(
            //         ConsentTypes.Gina
            //     ),
            //     choosesTheLefthandPath: getConsentRegistrationStep(
            //         ConsentTypes.Gina
            //     ),
            //     nextConsentStep,
            // });

            if (isPostEligibility) {
                await updateConsentAsync({
                    consentContext: consentRequirements[consentType],
                    accountConsentId: accountConsents[0].consentId,
                });
                // Refetch synchoronously before moving on to next page
                await refetchConsent();
                await refetchMostRecentSponsorship();
                changeStep(
                    getNextStep({
                        consentRequirements,
                        consentAcceptance: {
                            ...hasAcceptedConsent,
                            [consentType]: true,
                        },
                        consentFlow: getNextConsentTypeInFlow(consentType),
                    })
                );
            } else if (registrationFlow === RegistrationFlow.SkipSponsorship) {
                // Should only be reachable by CHDPP and parental
                const submission = await submitFlow({
                    registrationFlow,
                    minConsentVersions,
                });
                // Skip is a special case where we want to manually remove the requirements except CHDPP and parental
                changeStep(
                    getNextStep({
                        ...submission,
                        consentRequirements,
                        consentAcceptance: {
                            ...hasAcceptedConsent,
                            [ConsentTypes.Chdpp]: true,
                            ...(consentType === 'parental'
                                ? { [ConsentTypes.Parental]: true }
                                : {}),
                        },
                        consentFlow: [ConsentTypes.Chdpp],
                    })
                );
            } else if (
                registrationFlow === RegistrationFlow.LossOfSponsorship
            ) {
                // Should only be reachable by CHDPP and parental
                const submission = await submitFlow({
                    registrationFlow,
                    hipaaSignature,
                    minConsentVersions,
                });
                const nextStep = getNextStep({
                    ...submission,
                    consentAcceptance: {
                        ...hasAcceptedConsent,
                        [consentType]: true,
                    },
                });
                changeStep(nextStep);
            } else if (nextConsentStep) {
                changeStep(nextConsentStep);
            } else if (
                registrationFlow === RegistrationFlow.NewInviteeRegistration ||
                registrationFlow ===
                    RegistrationFlow.ExistingInviteeRegistration ||
                registrationFlow === RegistrationFlow.ChangeOfInviteeSponsorship
            ) {
                const submission = await submitFlow({
                    registrationFlow,
                    minConsentVersions,
                });
                changeStep(
                    getNextStep({
                        ...submission,
                        consentAcceptance: {
                            ...hasAcceptedConsent,
                            [consentType]: true,
                        },
                    })
                );
            } else if (isSSO && isSSOEligible) {
                const submission = await submitFlow({
                    registrationFlow,
                    isSSO,
                    willUseSSOTokenEligibility: true,
                    minConsentVersions,
                });
                changeStep(
                    getNextStep({
                        ...submission,
                        consentAcceptance: {
                            ...hasAcceptedConsent,
                            [consentType]: true,
                        },
                    })
                );
            } else {
                changeStep(RegistrationFlowStep.Eligibility);
            }
        } catch (error) {
            logger.error(
                `Error from ${consentType} step`,
                {
                    hasRequirement:
                        consentRequirements[consentType].hasRequirement,
                    isRequired: consentRequirements[consentType].hasRequirement,
                    isSSO,
                    registrationFlow,
                },
                error as Error
            );
        }
    };

    return (
        <>
            {showCancel && (
                <Button
                    trackButton="Cancel"
                    trackProps={trackProps}
                    variant={getCancelVariant(consentType)}
                    color="primary"
                    size="large"
                    onClick={onCancel}
                    sx={styleGrow}
                    disabled={!isFetched || submitIsPending}
                >
                    {formatMessage(commonMessages['common.cancel'])}
                </Button>
            )}
            <Button
                trackButton="Accept"
                trackProps={trackProps}
                variant="contained"
                color="primary"
                size="large"
                onClick={onAgree}
                disabled={!isFetched || submitIsPending}
                sx={styleGrow}
            >
                {formatMessage(getConsentTypeMessage('accept', consentType))}
            </Button>
        </>
    );
}

// The combination of our full screen content with an iframe causes a strange
// scrolling bug. This is a workaround for it.
const styleFixOverflow = {
    '& > div:first-of-type': {
        overflowY: 'initial',
    },
};

interface ConsentStepProps {
    consentType: CircleConsentTypes;
}
export const ConsentStep = ({ consentType }: ConsentStepProps) => {
    const currentLanguage = useCurrentLanguage();
    const {
        isSSO,
        isFetched,
        submitIsPending,
        changeStep,
        exitFlow,
        newSponsor,
        currentSponsorPartner,
        registrationFlow,
        accessCodeState,
        inviteCodeState,
        consentRequirements,
        isPostEligibility,
    } = useProvidedRegistrationFlowContext();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const { push } = useRouter();

    const { formatMessage } = useIntl();

    if (!isFetched && !submitIsPending) {
        return <FullWidthLinearProgress />;
    }

    const showBack =
        consentType === ConsentTypes.Parental || (!isSSO && !isPostEligibility);

    const showCancelTopRight = Boolean(
        !isPostEligibility &&
            ![
                RegistrationFlow.NewRegistration,
                RegistrationFlow.NewInviteeRegistration,
                RegistrationFlow.SkipSponsorship,
            ].includes(registrationFlow)
    );

    const skipCancel =
        consentType !== CircleConsentTypes.Gina &&
        consentType !== CircleConsentTypes.Parental;

    /** Show on GINA if we have an exitFlow() and are not SSO */
    const showCancel = Boolean(skipCancel || (exitFlow && !isSSO));

    const onBack = () => {
        if (consentType === ConsentTypes.Parental) {
            return changeStep(RegistrationFlowStep.AgeGender);
        }

        if (
            [
                RegistrationFlow.ChangeOfSponsorship,
                RegistrationFlow.ChangeOfInviteeSponsorship,
            ].includes(registrationFlow)
        ) {
            return changeStep(RegistrationFlowStep.ChangeSponsorship);
        }

        if (
            registrationFlow === RegistrationFlow.NewRegistration &&
            accessCodeState?.isAccessCodeFromQuery
        ) {
            return changeStep(RegistrationFlowStep.Eligibility);
        }

        if (registrationFlow === RegistrationFlow.LossOfSponsorship) {
            if (!accessCodeState?.accessCode) {
                return changeStep(RegistrationFlowStep.Hipaa);
            }
        }

        if (
            [
                RegistrationFlow.NewInviteeRegistration,
                RegistrationFlow.SkipSponsorship,
            ].includes(registrationFlow)
        ) {
            return changeStep(RegistrationFlowStep.SelectCountry);
        }

        return changeStep(RegistrationFlowStep.AccessCode);
    };

    const onCancel = () => {
        if (exitFlow) {
            exitFlow();
        } else if (
            // Reachable via loss of sponsorship screen (when we regain via access code)
            registrationFlow === RegistrationFlow.ChangeOfSponsorship &&
            accessCodeState?.isAccessCodeFromQuery
        ) {
            push(routes.dashboard);
        } else if (
            // Reachable via loss of sponsorship screen (when we regain via invite)
            registrationFlow === RegistrationFlow.ChangeOfInviteeSponsorship &&
            inviteCodeState?.isInviteCodeFromQuery
        ) {
            push(routes.dashboard);
        } else {
            setIsDialogOpen(true);
        }
    };

    const showCalmHealthDefault =
        registrationFlow === RegistrationFlow.SkipSponsorship;

    const trackProps: EventProperties = {
        ConsentType: consentType,
        ConsentVersion: `v${consentRequirements[consentType].requiredVersion}`,
        IsDependent: [
            RegistrationFlow.ChangeOfInviteeSponsorship,
            RegistrationFlow.NewInviteeRegistration,
            RegistrationFlow.ExistingInviteeRegistration,
        ].includes(registrationFlow),
        PartnerID: isPostEligibility
            ? currentSponsorPartner?.id
            : newSponsor?.id,
    };

    return (
        <RegistrationFlowContainer
            screenName="Consent"
            featureName="Consent"
            trackProps={trackProps}
            leftAction={
                showBack && (
                    <BackButtonView
                        trackButton="Back"
                        ButtonProps={{
                            trackProps,
                        }}
                        onClick={onBack}
                    />
                )
            }
            fullHeightContent
            rightAction={
                showCancelTopRight ? (
                    <InvertedButton
                        trackButton="Cancel"
                        trackProps={trackProps}
                        color="primary"
                        sx={styleCancelButton}
                        onClick={onCancel}
                        size="large"
                    >
                        {formatMessage(commonMessages['common.cancel'])}
                    </InvertedButton>
                ) : null
            }
            bottomActions={
                <Actions
                    consentType={consentType}
                    showCancel={showCancel}
                    onCancel={onCancel}
                    trackProps={trackProps}
                />
            }
            title={formatMessage(getConsentTypeMessage('heading', consentType))}
            sx={styleFixOverflow}
        >
            <ConsentDisplay
                sponsorPartner={newSponsor}
                consentContext={consentRequirements[consentType]}
                title={formatMessage(
                    getConsentTypeMessage('title', consentType),
                    {
                        name: showCalmHealthDefault
                            ? formatMessage(commonMessages['common.brand_name'])
                            : newSponsor?.displayName,
                    }
                )}
                imageOverride={
                    consentType === CircleConsentTypes.Parental && (
                        <ParentalConsentImage />
                    )
                }
                showCalmHealthDefault={showCalmHealthDefault}
                srTitle={formatMessage(
                    getConsentTypeMessage('srTitle', consentType)
                )}
                fallbackUrl={
                    routes.external.consentFallback?.[consentType]
                        ? routes.external.consentFallback[consentType](
                              currentLanguage
                          )
                        : undefined
                }
            />
            {isDialogOpen && (
                <ConsentRequiredDialog setIsDialogOpen={setIsDialogOpen} />
            )}
        </RegistrationFlowContainer>
    );
};
